import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  Popover,
  MenuItem,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  TableSortLabel,
  Checkbox,
  Divider,
  LinearProgress,
  Badge
} from '@mui/material';

import {
  Search,
  FilterList,
  CheckCircle,
  Pending,
  Cancel,
  Download,
  Refresh,
  Clear,
  Store,
  Assignment,
  Verified,
  CalendarToday,
  ThumbUp,
  ThumbUpAlt,
  History,
  Inventory,
  Receipt,
  Warning,
  Info,
  EventNote,
  AccessTime,
  Block
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import Sidebar from '../../components/sidebars/Sidebar_auditor';
import TopNav from '../../components/top_bars/TopNav_inventory';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.background.paper,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
  overflowX: 'auto',
  overflowY: 'auto',
  position: 'relative',
  maxWidth: '100%',
  maxHeight: 'calc(100vh - 350px)', // Limit height to prevent overstretching
  minHeight: '400px',
  '& .MuiTable-root': {
    minWidth: 800,
    width: '100%',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 700,
    fontSize: '0.85rem',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backdropFilter: 'blur(8px)',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
    transition: 'background-color 0.2s ease',
  }
}));

const GlowingButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
  }
}));

// Main content wrapper with proper scrolling
const MainContentWrapper = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  height: '100vh',
  overflowY: 'auto',
  overflowX: 'hidden',
  position: 'relative',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.common.black, 0.05),
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.4),
    borderRadius: '4px',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.6),
    },
  },
}));

const AuditorApproval = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [auditorName, setAuditorName] = useState('');

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10); // Changed to 10
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  
  // Selection states for bulk approval
  const [selectedSupplies, setSelectedSupplies] = useState([]);
  
  // Supplies states
  const [pendingSupplies, setPendingSupplies] = useState([]);
  const [approvedSupplies, setApprovedSupplies] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [totalApproved, setTotalApproved] = useState(0);
  const [totalInventory, setTotalInventory] = useState(0);
  const [totalRequisitions, setTotalRequisitions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // Backdate statistics
  const [backdateStats, setBackdateStats] = useState({
    totalBackdated: 0,
    criticalBackdated: 0,
    warningBackdated: 0
  });
  
  // Dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [bulkApproveDialogOpen, setBulkApproveDialogOpen] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);
  const [backdateWarning, setBackdateWarning] = useState(null);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Sorting function
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Calculate backdate statistics from pending supplies
  const calculateBackdateStats = useCallback((supplies) => {
    const backdatedItems = supplies.filter(s => s.is_backdated === true);
    const totalBackdated = backdatedItems.length;
    
    const criticalBackdated = backdatedItems.filter(s => s.days_since_backdate >= 4).length;
    const warningBackdated = backdatedItems.filter(s => s.days_since_backdate === 3).length;
    
    setBackdateStats({
      totalBackdated,
      criticalBackdated,
      warningBackdated
    });
    
    return backdatedItems;
  }, []);

  // Fetch pending supplies with pagination
  const fetchPendingSupplies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_pending_supplies.php?${params}`);
      if (response.data.success) {
        setPendingSupplies(response.data.data);
        setTotalPending(response.data.total);
        calculateBackdateStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching pending supplies:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch pending supplies',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, dateRange, page, rowsPerPage, calculateBackdateStats]);

  // Fetch approved supplies with pagination
  const fetchApprovedSupplies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_approved_supplies.php?${params}`);
      if (response.data.success) {
        setApprovedSupplies(response.data.data);
        setTotalApproved(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching approved supplies:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch approved supplies',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, dateRange, page, rowsPerPage]);

  // Fetch inventory items
  const fetchInventoryItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_inventory_items.php?${params}`);
      if (response.data.success) {
        setInventoryItems(response.data.data);
        setTotalInventory(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch inventory items',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, page, rowsPerPage]);

  // Fetch requisitions
  const fetchRequisitions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_requisitions.php?${params}`);
      if (response.data.success) {
        setRequisitions(response.data.data);
        setTotalRequisitions(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching requisitions:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch requisitions',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, dateRange, page, rowsPerPage]);

  // Fetch categories
  // Fetch categories - with better error handling
const fetchCategories = useCallback(async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/inventory/get_categories.php`);
        if (response.data.success) {
            setCategories(response.data.categories);
        } else {
            console.warn('Failed to fetch categories:', response.data.message);
            // Fallback to default categories if API fails
            setCategories(['Restaurant', 'Lounge', 'Arcade', 'Cosmetics', 'Salon', 'Fashion', 'Spa', 'Hall', 'Admin', 'Others']);
        }
    } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback to default categories on error
        setCategories(['Restaurant', 'Lounge', 'Arcade', 'Cosmetics', 'Salon', 'Fashion', 'Spa', 'Hall', 'Admin', 'Others']);
    }
}, []);

  // Effect to fetch data based on active tab
  useEffect(() => {
    if (activeTab === 0) {
      fetchPendingSupplies();
    } else if (activeTab === 1) {
      fetchApprovedSupplies();
    } else if (activeTab === 2) {
      fetchInventoryItems();
    } else if (activeTab === 3) {
      fetchRequisitions();
    }
    fetchCategories();
  }, [activeTab, fetchPendingSupplies, fetchApprovedSupplies, fetchInventoryItems, fetchRequisitions, fetchCategories]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, selectedCategory, dateRange]);

  // Clear selection when tab changes
  useEffect(() => {
    setSelectedSupplies([]);
    setPage(0);
  }, [activeTab]);

  // Get auditor name
  useEffect(() => {
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('email');
    
    if (userName) {
      setAuditorName(userName);
    } else if (userEmail) {
      setAuditorName(userEmail);
    } else {
      setAuditorName('Auditor');
    }
  }, []);

  // Check if a supply is eligible for approval (backdate validation)
  const isSupplyApprovalEligible = (supply) => {
    if (!supply) return false;
    if (!supply.is_backdated) return true;
    
    const daysSinceBackdate = supply.days_since_backdate || 0;
    return daysSinceBackdate < 4;
  };

  // Get backdate warning message for a supply
  const getBackdateWarningMessage = (supply) => {
    if (!supply || !supply.is_backdated) return null;
    
    const daysSinceBackdate = supply.days_since_backdate || 0;
    
    if (daysSinceBackdate >= 4) {
      return {
        severity: 'error',
        title: 'EXPIRED BACKDATE WINDOW',
        message: `This record is backdated to ${supply.date_received} (${daysSinceBackdate} days ago). Approval must happen within 4 days of the recorded date. This supply CANNOT be approved.`,
        canApprove: false
      };
    } else if (daysSinceBackdate === 3) {
      return {
        severity: 'warning',
        title: 'URGENT: Backdate Window Closing',
        message: `This record is backdated to ${supply.date_received} (${daysSinceBackdate} days ago). You have only ${4 - daysSinceBackdate} day(s) left to approve this supply.`,
        canApprove: true
      };
    } else if (daysSinceBackdate >= 2) {
      return {
        severity: 'info',
        title: 'Backdated Record',
        message: `This record is backdated to ${supply.date_received} (${daysSinceBackdate} days ago). Reason: ${supply.backdate_reason || 'No reason provided'}`,
        canApprove: true
      };
    }
    
    return {
      severity: 'info',
      title: 'Backdated Record',
      message: `This record is backdated to ${supply.date_received}. Reason: ${supply.backdate_reason || 'No reason provided'}`,
      canApprove: true
    };
  };

  // Approve single supply with backdate validation
  const handleApproveSupply = async () => {
    if (!selectedSupply) return;
    
    if (!isSupplyApprovalEligible(selectedSupply)) {
      setSnackbar({
        open: true,
        message: `Cannot approve "${selectedSupply.item_name}". This backdated record is ${selectedSupply.days_since_backdate} days old and exceeds the 4-day approval window.`,
        severity: 'error'
      });
      handleCloseApproveDialog();
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: selectedSupply.id,
        approved_by: auditorName
      };

      const response = await axios({
        method: 'put',
        url: `${API_BASE_URL}/inventory/approve_supply.php`,
        data: payload,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        let successMessage = `"${selectedSupply.item_name}" approved successfully`;
        if (selectedSupply.is_backdated) {
          successMessage += ` (Backdated record - Date: ${selectedSupply.date_received})`;
        }
        setSnackbar({
          open: true,
          message: successMessage,
          severity: 'success'
        });
        handleCloseApproveDialog();
        fetchPendingSupplies();
        fetchApprovedSupplies();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error approving supply:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to approve supply',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Approve multiple supplies with backdate validation
  const handleBulkApprove = async () => {
    if (selectedSupplies.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one supply to approve',
        severity: 'warning'
      });
      return;
    }

    const eligibleSupplies = selectedSupplies.filter(id => {
      const supply = pendingSupplies.find(s => s.id === id);
      return supply && isSupplyApprovalEligible(supply);
    });
    
    const ineligibleSupplies = selectedSupplies.filter(id => {
      const supply = pendingSupplies.find(s => s.id === id);
      return supply && !isSupplyApprovalEligible(supply);
    });

    if (eligibleSupplies.length === 0) {
      setSnackbar({
        open: true,
        message: 'None of the selected supplies are eligible for approval (backdated records exceeding 4 days cannot be approved).',
        severity: 'error'
      });
      setBulkApproveDialogOpen(false);
      return;
    }

    if (ineligibleSupplies.length > 0) {
      setSnackbar({
        open: true,
        message: `${ineligibleSupplies.length} supply(s) were skipped due to backdate window expiry. Only ${eligibleSupplies.length} supply(s) will be approved.`,
        severity: 'warning'
      });
    }

    setLoading(true);
    try {
      const payload = {
        ids: eligibleSupplies,
        approved_by: auditorName
      };

      const response = await axios({
        method: 'put',
        url: `${API_BASE_URL}/inventory/approve_multiple_supplies.php`,
        data: payload,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message + (ineligibleSupplies.length > 0 ? ` (${ineligibleSupplies.length} skipped - backdate window expired)` : ''),
          severity: 'success'
        });
        setSelectedSupplies([]);
        setBulkApproveDialogOpen(false);
        fetchPendingSupplies();
        fetchApprovedSupplies();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error bulk approving supplies:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to approve supplies',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Open approve dialog with backdate warning check
  const handleOpenApproveDialog = (supply) => {
    setSelectedSupply(supply);
    const warning = getBackdateWarningMessage(supply);
    setBackdateWarning(warning);
    setApproveDialogOpen(true);
  };

  // Export to Excel
  const handleExportToExcel = () => {
    let exportData = [];
    let fileName = '';
    
    if (activeTab === 0) {
      exportData = pendingSupplies.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category,
        'Unit': item.unit_of_measurement,
        'Quantity': item.quantity_received,
        'Total Invoice Value (₦)': item.total_invoice_value,
        'Date Received': item.date_received,
        'Requested By': item.added_by_staffid,
        'Status': 'Pending',
        'Is Backdated': item.is_backdated ? 'Yes' : 'No',
        'Backdate Reason': item.backdate_reason || 'N/A',
        'Days Since Backdate': item.days_since_backdate || 'N/A',
        'Backdated By': item.backdated_by || 'N/A'
      }));
      fileName = 'pending_supplies';
    } else if (activeTab === 1) {
      exportData = approvedSupplies.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category,
        'Unit': item.unit_of_measurement,
        'Quantity': item.quantity_received,
        'Total Invoice Value (₦)': item.total_invoice_value,
        'Date Received': item.date_received,
        'Requested By': item.added_by_staffid,
        'Approved By': item.checked_by,
        'Approval Date': item.updated_at,
        'Is Backdated': item.is_backdated ? 'Yes' : 'No',
        'Backdate Reason': item.backdate_reason || 'N/A'
      }));
      fileName = 'approved_supplies';
    } else if (activeTab === 2) {
      exportData = inventoryItems.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category,
        'Unit': item.unit_of_measurement,
        'Current Stock': item.quantity_available,
        'Total Received': item.quantity_received,
        'Total Issued': item.quantity_issued,
        'Low Stock Threshold': item.lowstock_threshold,
        'Storage Location': item.storage_location || 'N/A',
        'Date Received': item.date_received
      }));
      fileName = 'inventory_items';
    } else if (activeTab === 3) {
      exportData = requisitions.map(item => ({
        'Item Name': item.item_name,
        'Quantity Requested': item.quantity_requested,
        'Unit': item.unit_of_measurement,
        'Request Date': item.request_date,
        'Requested By': item.requested_by,
        'Department': item.department,
        'Status': item.status,
        'Notes': item.notes || 'N/A'
      }));
      fileName = 'requisitions';
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    setSnackbar({
      open: true,
      message: 'Export successful!',
      severity: 'success'
    });
  };

  const handleCloseApproveDialog = () => {
    setApproveDialogOpen(false);
    setSelectedSupply(null);
    setBackdateWarning(null);
  };

  const handleOpenBulkApproveDialog = () => {
    if (selectedSupplies.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one supply to approve',
        severity: 'warning'
      });
      return;
    }
    
    const ineligibleCount = selectedSupplies.filter(id => {
      const supply = pendingSupplies.find(s => s.id === id);
      return supply && !isSupplyApprovalEligible(supply);
    }).length;
    
    if (ineligibleCount > 0) {
      setSnackbar({
        open: true,
        message: `${ineligibleCount} selected supply(s) are not eligible for approval (backdated > 4 days). They will be skipped.`,
        severity: 'warning'
      });
    }
    
    setBulkApproveDialogOpen(true);
  };

  const handleCloseBulkApproveDialog = () => {
    setBulkApproveDialogOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
    setSearchQuery('');
    setSelectedCategory('all');
    setDateRange({ start: '', end: '' });
    setSelectedSupplies([]);
    setOrderBy('');
    setOrder('asc');
  };

  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setDateRange({ start: '', end: '' });
    setPage(0);
    handleFilterClose();
  };

  // Handle select all/none
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = pendingSupplies
        .filter((supply) => isSupplyApprovalEligible(supply))
        .map((supply) => supply.id);
      setSelectedSupplies(newSelected);
    } else {
      setSelectedSupplies([]);
    }
  };

  // Handle single select
  const handleSelect = (id) => {
    const supply = pendingSupplies.find(s => s.id === id);
    if (!supply) return;
    
    if (!isSupplyApprovalEligible(supply)) {
      setSnackbar({
        open: true,
        message: 'This supply cannot be selected for approval as it exceeds the 4-day backdate window.',
        severity: 'warning'
      });
      return;
    }
    
    const selectedIndex = selectedSupplies.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedSupplies, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedSupplies.slice(1));
    } else if (selectedIndex === selectedSupplies.length - 1) {
      newSelected = newSelected.concat(selectedSupplies.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedSupplies.slice(0, selectedIndex),
        selectedSupplies.slice(selectedIndex + 1),
      );
    }

    setSelectedSupplies(newSelected);
  };

  // Sort data
  const sortData = (data) => {
    if (!orderBy || !data) return data;
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'quantity_received' || orderBy === 'total_invoice_value' || orderBy === 'quantity_available' || 
          orderBy === 'quantity_issued' || orderBy === 'quantity_requested' || orderBy === 'days_since_backdate') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (orderBy === 'date_received' || orderBy === 'request_date' || orderBy === 'updated_at' || orderBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getCurrentData = () => {
    if (activeTab === 0) return sortData(pendingSupplies);
    if (activeTab === 1) return sortData(approvedSupplies);
    if (activeTab === 2) return sortData(inventoryItems);
    if (activeTab === 3) return sortData(requisitions);
    return [];
  };

  const filteredData = getCurrentData();
  const getTotalItems = () => {
    if (activeTab === 0) return totalPending;
    if (activeTab === 1) return totalApproved;
    if (activeTab === 2) return totalInventory;
    if (activeTab === 3) return totalRequisitions;
    return 0;
  };
  const totalItems = getTotalItems();

  // Statistics
  const statistics = useMemo(() => {
    const pendingCount = totalPending;
    const approvedCount = totalApproved;
    const inventoryCount = totalInventory;
    const requisitionCount = totalRequisitions;
    const selectedCount = selectedSupplies.length;
    
    return { pendingCount, approvedCount, inventoryCount, requisitionCount, selectedCount };
  }, [totalPending, totalApproved, totalInventory, totalRequisitions, selectedSupplies]);

  // Render Pending Supplies Table
  const renderPendingTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" sx={{ width: 50 }}>
              <Checkbox
                indeterminate={selectedSupplies.length > 0 && selectedSupplies.length < pendingSupplies.filter(s => isSupplyApprovalEligible(s)).length}
                checked={pendingSupplies.filter(s => isSupplyApprovalEligible(s)).length > 0 && 
                         selectedSupplies.length === pendingSupplies.filter(s => isSupplyApprovalEligible(s)).length}
                onChange={handleSelectAll}
              />
            </TableCell>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_received'}
                direction={orderBy === 'quantity_received' ? order : 'asc'}
                onClick={() => handleSort('quantity_received')}
              >
                Quantity
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 150 }}>
              <TableSortLabel
                active={orderBy === 'total_invoice_value'}
                direction={orderBy === 'total_invoice_value' ? order : 'asc'}
                onClick={() => handleSort('total_invoice_value')}
              >
                Total Invoice (₦)
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'date_received'}
                direction={orderBy === 'date_received' ? order : 'asc'}
                onClick={() => handleSort('date_received')}
              >
                Date Received
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Backdate Info</TableCell>
            <TableCell sx={{ minWidth: 150 }}>Requested By</TableCell>
            <TableCell align="center" sx={{ minWidth: 120 }}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : !filteredData || filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No pending supplies found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((item) => {
              const isBackdated = item?.is_backdated === true;
              const daysSinceBackdate = item?.days_since_backdate || 0;
              const isEligible = isSupplyApprovalEligible(item);
              const isCritical = isBackdated && daysSinceBackdate >= 4;
              
              return (
                <TableRow 
                  key={item.id} 
                  hover 
                  selected={selectedSupplies.indexOf(item.id) !== -1}
                  sx={{
                    ...(isCritical && {
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.error.main, 0.12),
                      }
                    }),
                    ...(isBackdated && !isCritical && daysSinceBackdate === 3 && {
                      backgroundColor: alpha(theme.palette.warning.main, 0.08),
                    })
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedSupplies.indexOf(item.id) !== -1}
                      onChange={() => handleSelect(item.id)}
                      disabled={!isEligible}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                        <Pending sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.category} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{item.unit_of_measurement}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500}>
                      {Number(item.quantity_received).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} color="primary.main">
                      ₦{Number(item.total_invoice_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption">{item.date_received}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {isBackdated ? (
                      <Tooltip 
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>Backdate Details:</Typography>
                            <Typography variant="caption" display="block">Date: {item.date_received}</Typography>
                            <Typography variant="caption" display="block">Days ago: {daysSinceBackdate}</Typography>
                            <Typography variant="caption" display="block">Reason: {item.backdate_reason || 'N/A'}</Typography>
                            <Typography variant="caption" display="block">Backdated by: {item.backdated_by || 'N/A'}</Typography>
                          </Box>
                        }
                      >
                        <Chip 
                          icon={<History fontSize="small" />}
                          label={`Backdated (${daysSinceBackdate}d)`}
                          size="small"
                          color={isCritical ? "error" : daysSinceBackdate === 3 ? "warning" : "info"}
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Chip label="Current" size="small" variant="outlined" />
                    )}
                    {isBackdated && daysSinceBackdate === 3 && (
                      <LinearProgress 
                        variant="determinate" 
                        value={75} 
                        sx={{ mt: 0.5, height: 2, borderRadius: 1 }}
                        color="warning"
                      />
                    )}
                    {isBackdated && daysSinceBackdate >= 4 && (
                      <LinearProgress 
                        variant="determinate" 
                        value={100} 
                        sx={{ mt: 0.5, height: 2, borderRadius: 1 }}
                        color="error"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`Requested by: ${item.added_by_staffid}`}>
                      <Chip 
                        label={item.added_by_staffid || 'Unknown'}
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <GlowingButton
                      variant="contained"
                      color={isEligible ? "success" : "error"}
                      size="small"
                      startIcon={isEligible ? <ThumbUp /> : <Block />}
                      onClick={() => handleOpenApproveDialog(item)}
                      disabled={!isEligible}
                      sx={{ minWidth: 100 }}
                    >
                      {isEligible ? "Approve" : "Expired"}
                    </GlowingButton>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  // Render Approved Supplies Table
  const renderApprovedTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_received'}
                direction={orderBy === 'quantity_received' ? order : 'asc'}
                onClick={() => handleSort('quantity_received')}
              >
                Quantity
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 150 }}>
              <TableSortLabel
                active={orderBy === 'total_invoice_value'}
                direction={orderBy === 'total_invoice_value' ? order : 'asc'}
                onClick={() => handleSort('total_invoice_value')}
              >
                Total Invoice (₦)
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'date_received'}
                direction={orderBy === 'date_received' ? order : 'asc'}
                onClick={() => handleSort('date_received')}
              >
                Date Received
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 100 }}>Backdated</TableCell>
            <TableCell sx={{ minWidth: 150 }}>Added By</TableCell>
            <TableCell sx={{ minWidth: 150 }}>Approved By</TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'updated_at'}
                direction={orderBy === 'updated_at' ? order : 'asc'}
                onClick={() => handleSort('updated_at')}
              >
                Approval Date
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : !filteredData || filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No approved supplies found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((item) => {
              const isBackdated = item?.is_backdated === true;
              
              return (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                        <Verified sx={{ fontSize: 18, color: theme.palette.success.main }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.category} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{item.unit_of_measurement}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500}>
                      {Number(item.quantity_received).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      ₦{Number(item.total_invoice_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption">{item.date_received}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {isBackdated ? (
                      <Tooltip title={item.backdate_reason || 'No reason provided'}>
                        <Chip 
                          icon={<History fontSize="small" />}
                          label="Yes" 
                          size="small" 
                          color="info"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Chip label="No" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={item.added_by_staffid || 'Unknown'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={item.checked_by || 'Auditor'} 
                      size="small" 
                      color="success"
                      variant="outlined"
                      icon={<Verified sx={{ fontSize: 14 }} />}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  // Render Inventory Table
  const renderInventoryTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_available'}
                direction={orderBy === 'quantity_available' ? order : 'asc'}
                onClick={() => handleSort('quantity_available')}
              >
                Current Stock
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_received'}
                direction={orderBy === 'quantity_received' ? order : 'asc'}
                onClick={() => handleSort('quantity_received')}
              >
                Total Received
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_issued'}
                direction={orderBy === 'quantity_issued' ? order : 'asc'}
                onClick={() => handleSort('quantity_issued')}
              >
                Total Issued
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>Low Stock Alert</TableCell>
            <TableCell sx={{ minWidth: 150 }}>Storage Location</TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'date_received'}
                direction={orderBy === 'date_received' ? order : 'asc'}
                onClick={() => handleSort('date_received')}
              >
                Date Received
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : !filteredData || filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No inventory items found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((item) => {
              const isLowStock = item.quantity_available <= item.lowstock_threshold && item.quantity_available > 0;
              const isOutOfStock = item.quantity_available <= 0;
              
              return (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                        <Inventory sx={{ fontSize: 18, color: theme.palette.info.main }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.category || 'Uncategorized'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{item.unit_of_measurement || 'N/A'}</TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      fontWeight={500}
                      color={isOutOfStock ? 'error.main' : isLowStock ? 'warning.main' : 'text.primary'}
                    >
                      {Number(item.quantity_available).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {Number(item.quantity_received).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {Number(item.quantity_issued).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={item.lowstock_threshold || 5}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={item.storage_location || 'Not specified'} 
                      size="small" 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption">{item.date_received}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  // Render Requisitions Table
  const renderRequisitionsTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_requested'}
                direction={orderBy === 'quantity_requested' ? order : 'asc'}
                onClick={() => handleSort('quantity_requested')}
              >
                Requested Qty
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'request_date'}
                direction={orderBy === 'request_date' ? order : 'asc'}
                onClick={() => handleSort('request_date')}
              >
                Request Date
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>Requested By</TableCell>
            <TableCell sx={{ minWidth: 120 }}>Department</TableCell>
            <TableCell sx={{ minWidth: 120 }}>Status</TableCell>
            <TableCell sx={{ minWidth: 200 }}>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : !filteredData || filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No requisitions found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                      <Receipt sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500}>
                    {Number(item.quantity_requested).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>{item.unit_of_measurement || 'N/A'}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption">{item.request_date}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={item.requested_by} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip label={item.department || 'N/A'} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={item.status}
                    size="small"
                    color={
                      item.status === 'Approved by GM' ? 'success' : 
                      item.status === 'Pending' ? 'warning' : 
                      item.status === 'Rejected' ? 'error' : 'info'
                    }
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {item.notes || 'N/A'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  const renderCurrentTable = () => {
    if (activeTab === 0) return renderPendingTable();
    if (activeTab === 1) return renderApprovedTable();
    if (activeTab === 2) return renderInventoryTable();
    if (activeTab === 3) return renderRequisitionsTable();
    return null;
  };

  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default, minHeight: '100vh', overflow: 'hidden' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <MainContentWrapper>
        <Box sx={{
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 0 : 0,
          transition: 'margin 0.3s ease-in-out',
          maxWidth: '1600px',
          mx: 'auto',
          width: '100%',
          overflowX: 'hidden'
        }}>
          <TopNav
            onLogout={() => {
              localStorage.removeItem('authToken');
              localStorage.removeItem('userProfile');
            }}
            onNotificationClick={() => console.log('Notifications')}
            toggleSidebar={toggleSidebar}
          />
         
          <Box sx={{ mt: 3, width: '100%', overflowX: 'hidden' }}>
            <Paper elevation={0} sx={{ borderRadius: '20px', mb: 3, overflow: 'hidden', width: '100%' }}>
              {/* Header */}
              <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                <Typography variant="h5" fontWeight={700}>
                  Auditor Approval Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Welcome, {auditorName}. Review and approve supply requests, view inventory and requisitions.
                </Typography>
              </Box>

              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant={isMobile ? "scrollable" : "standard"}
                scrollButtons={isMobile ? "auto" : false}
                sx={{
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    py: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    }
                  },
                  '& .Mui-selected': {
                    color: theme.palette.primary.main,
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  }
                }}
              >
                <Tab 
                  icon={
                    <Badge badgeContent={backdateStats.criticalBackdated} color="error" invisible={backdateStats.criticalBackdated === 0}>
                      <Pending />
                    </Badge>
                  } 
                  iconPosition="start" 
                  label={`Pending (${statistics.pendingCount})`} 
                />
                <Tab icon={<History />} iconPosition="start" label={`Approved (${statistics.approvedCount})`} />
                <Tab icon={<Inventory />} iconPosition="start" label={`Inventory (${statistics.inventoryCount})`} />
                <Tab icon={<Receipt />} iconPosition="start" label={`Requisitions (${statistics.requisitionCount})`} />
              </Tabs>

              {/* Backdate Alert Banner */}
              {activeTab === 0 && (backdateStats.criticalBackdated > 0 || backdateStats.warningBackdated > 0) && (
                <Alert 
                  severity={backdateStats.criticalBackdated > 0 ? "error" : "warning"}
                  icon={<Warning />}
                  sx={{ m: 2, borderRadius: '12px' }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>
                    Backdate Approval Notice
                  </Typography>
                  <Typography variant="body2">
                    {backdateStats.criticalBackdated > 0 && 
                      `${backdateStats.criticalBackdated} backdated supply(s) have exceeded the 4-day approval window and CANNOT be approved. `}
                    {backdateStats.warningBackdated > 0 && 
                      `${backdateStats.warningBackdated} backdated supply(s) have only 1 day left for approval.`}
                  </Typography>
                </Alert>
              )}

              {/* Statistics Cards */}
              <Grid container spacing={2} sx={{ p: 3, backgroundColor: alpha(theme.palette.primary.main, 0.01) }}>
                {activeTab === 0 && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <StyledCard>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                Pending Approvals
                              </Typography>
                              <Typography variant="h4" fontWeight={700} mt={1} color="warning.main">
                                {statistics.pendingCount}
                              </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                              <Pending />
                            </Avatar>
                          </Box>
                        </CardContent>
                      </StyledCard>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <StyledCard>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                Backdated Records
                              </Typography>
                              <Typography variant="h4" fontWeight={700} mt={1} color="info.main">
                                {backdateStats.totalBackdated}
                              </Typography>
                              {backdateStats.criticalBackdated > 0 && (
                                <Typography variant="caption" color="error.main" display="block">
                                  {backdateStats.criticalBackdated} expired
                                </Typography>
                              )}
                            </Box>
                            <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                              <History />
                            </Avatar>
                          </Box>
                        </CardContent>
                      </StyledCard>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <StyledCard>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                Selected for Approval
                              </Typography>
                              <Typography variant="h4" fontWeight={700} mt={1} color="primary.main">
                                {statistics.selectedCount}
                              </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                              <ThumbUpAlt />
                            </Avatar>
                          </Box>
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  </>
                )}
                {activeTab === 1 && (
                  <Grid item xs={12}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Total Approved Supplies
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="success.main">
                              {statistics.approvedCount}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                            <Verified />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                )}
                {activeTab === 2 && (
                  <Grid item xs={12}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Total Inventory Items
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="info.main">
                              {statistics.inventoryCount}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                            <Inventory />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                )}
                {activeTab === 3 && (
                  <Grid item xs={12}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Total Requisitions
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="secondary.main">
                              {statistics.requisitionCount}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main }}>
                            <Receipt />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                )}
              </Grid>

              {/* Search and Filters Bar */}
              <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      placeholder="Search by item name, category, or requester..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchQuery('')}>
                              <Clear fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={8}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                      <GlowingButton
                        variant="outlined"
                        startIcon={<FilterList />}
                        onClick={handleFilterClick}
                      >
                        Filters
                      </GlowingButton>
                      
                      {activeTab === 0 && (
                        <GlowingButton
                          variant="contained"
                          color="success"
                          startIcon={<ThumbUpAlt />}
                          onClick={handleOpenBulkApproveDialog}
                          disabled={selectedSupplies.length === 0}
                        >
                          Bulk Approve ({selectedSupplies.length})
                        </GlowingButton>
                      )}
                      
                      <GlowingButton
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleExportToExcel}
                      >
                        Export
                      </GlowingButton>
                      
                      <IconButton onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('all');
                        setDateRange({ start: '', end: '' });
                        if (activeTab === 0) {
                          fetchPendingSupplies();
                        } else if (activeTab === 1) {
                          fetchApprovedSupplies();
                        } else if (activeTab === 2) {
                          fetchInventoryItems();
                        } else if (activeTab === 3) {
                          fetchRequisitions();
                        }
                      }}>
                        <Refresh />
                      </IconButton>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              {/* Active Filters Display */}
              {(selectedCategory !== 'all' || dateRange.start || dateRange.end) && (
                <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Active Filters:</Typography>
                  {selectedCategory !== 'all' && (
                    <Chip label={`Category: ${selectedCategory}`} onDelete={() => setSelectedCategory('all')} size="small" />
                  )}
                  {dateRange.start && (
                    <Chip label={`From: ${dateRange.start}`} onDelete={() => setDateRange({ ...dateRange, start: '' })} size="small" />
                  )}
                  {dateRange.end && (
                    <Chip label={`To: ${dateRange.end}`} onDelete={() => setDateRange({ ...dateRange, end: '' })} size="small" />
                  )}
                  <Button size="small" onClick={clearFilters} sx={{ ml: 1 }}>Clear All</Button>
                </Box>
              )}

              {/* Table */}
              {renderCurrentTable()}
            </Paper>
          </Box>
        </Box>
      </MainContentWrapper>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 2, width: 320, borderRadius: '16px' } }}
      >
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Filters</Typography>
        
        {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} label="Category">
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        {(activeTab === 0 || activeTab === 1 || activeTab === 3) && (
          <>
            <TextField
              fullWidth size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }}
              value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }}
              value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              sx={{ mb: 2 }}
            />
          </>
        )}
        
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={clearFilters} size="small">Clear All</Button>
          <Button variant="contained" onClick={handleFilterClose} size="small">Apply</Button>
        </Stack>
      </Popover>

      {/* Single Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={handleCloseApproveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          {backdateWarning && (
            <Alert 
              severity={backdateWarning.severity} 
              icon={backdateWarning.severity === 'error' ? <Block /> : <Warning />}
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle2" fontWeight={600}>{backdateWarning.title}</Typography>
              <Typography variant="body2">{backdateWarning.message}</Typography>
            </Alert>
          )}
          
          <Typography>
            Are you sure you want to approve <strong>"{selectedSupply?.item_name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Quantity: {selectedSupply?.quantity_received} {selectedSupply?.unit_of_measurement}<br />
            Total Value: ₦{Number(selectedSupply?.total_invoice_value).toLocaleString()}
            {selectedSupply?.is_backdated && (
              <>
                <br />
                <Box component="span" display="flex" alignItems="center" gap={0.5} mt={0.5}>
                  <History sx={{ fontSize: 14 }} />
                  Record Date: {selectedSupply?.date_received}
                </Box>
                {selectedSupply?.backdate_reason && (
                  <Box component="span" display="flex" alignItems="flex-start" gap={0.5} mt={0.5}>
                    <Info sx={{ fontSize: 14 }} />
                    Reason: {selectedSupply?.backdate_reason}
                  </Box>
                )}
              </>
            )}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            This action will mark the supply as approved and it will be added to the inventory.
            {selectedSupply?.is_backdated && " The backdated date will be preserved in the inventory record."}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApproveDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={backdateWarning?.canApprove === false ? "error" : "success"}
            onClick={handleApproveSupply}
            disabled={loading || (backdateWarning?.canApprove === false)}
            startIcon={backdateWarning?.canApprove === false ? <Block /> : <ThumbUp />}
          >
            {loading ? <CircularProgress size={24} /> : (backdateWarning?.canApprove === false ? "Cannot Approve" : "Confirm Approval")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Approve Confirmation Dialog */}
      <Dialog open={bulkApproveDialogOpen} onClose={handleCloseBulkApproveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Bulk Approval</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve <strong>{selectedSupplies.length} supply(s)</strong>?
          </Typography>
          
          {selectedSupplies.filter(id => {
            const supply = pendingSupplies.find(s => s.id === id);
            return supply?.is_backdated && supply?.days_since_backdate >= 3;
          }).length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {selectedSupplies.filter(id => {
                const supply = pendingSupplies.find(s => s.id === id);
                return supply?.is_backdated && supply?.days_since_backdate >= 4;
              }).length > 0 && (
                <Typography variant="body2">
                  ⚠️ Some selected supplies have exceeded the 4-day backdate window and will be skipped.
                </Typography>
              )}
              {selectedSupplies.filter(id => {
                const supply = pendingSupplies.find(s => s.id === id);
                return supply?.is_backdated && supply?.days_since_backdate === 3;
              }).length > 0 && (
                <Typography variant="body2">
                  ⏰ Some selected supplies have only 1 day left for approval.
                </Typography>
              )}
            </Alert>
          )}
          
          <Alert severity="info" sx={{ mt: 2 }}>
            This action will approve all eligible selected supplies and add them to the inventory.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>Selected Items:</Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
              {pendingSupplies
                .filter(s => selectedSupplies.includes(s.id))
                .map(s => (
                  <Typography key={s.id} variant="body2" sx={{ py: 0.5 }}>
                    • {s.item_name} ({s.quantity_received} {s.unit_of_measurement})
                    {s.is_backdated && ` [Backdated: ${s.date_received}]`}
                    {s.is_backdated && s.days_since_backdate >= 4 && ` ⚠️ EXPIRED`}
                  </Typography>
                ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkApproveDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleBulkApprove}
            disabled={loading}
            startIcon={<ThumbUpAlt />}
          >
            {loading ? <CircularProgress size={24} /> : `Approve Eligible (${selectedSupplies.filter(id => {
              const supply = pendingSupplies.find(s => s.id === id);
              return supply && isSupplyApprovalEligible(supply);
            }).length})`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AuditorApproval;